import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { detailMessage, humanize } from "../lib/driverService";

const SCOPES = [{ key: "today", label: "My day" }, { key: "upcoming", label: "Upcoming" }, { key: "past", label: "Past" }];

export default function DriverWorkspace() {
  const [me, setMe] = useState(null);
  const [scope, setScope] = useState("today");
  const [trips, setTrips] = useState(null);
  const [denied, setDenied] = useState(false);

  const load = useCallback(() => {
    api.get("/driver-workspace/me").then((r) => setMe(r.data)).catch((err) => {
      if (err?.response?.status === 403) setDenied(true); else toast.error(detailMessage(err));
    });
    api.get("/driver-workspace/trips", { params: { scope } }).then((r) => setTrips(r.data.items)).catch(() => setTrips([]));
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  if (denied) return <div className="rounded-xl border bg-white p-6 text-slate-600" data-testid="dw-denied">This page is for drivers.</div>;
  if (!me) return <div className="text-slate-500">Loading…</div>;

  const call = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); } catch (err) { toast.error(detailMessage(err)); }
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="driver-workspace">
      <div>
        <h1 className="font-display text-2xl font-bold">Hi, {me.display_name}</h1>
        <p className="text-sm text-slate-600">Duty: {humanize(me.duty_status)} · Safety: {humanize(me.safety_status)}</p>
      </div>

      {me.documents.some((d) => d.missing || (d.days_to_expiry !== null && d.days_to_expiry <= 60)) && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="dw-documents-alert">
          {me.documents.filter((d) => d.missing || (d.days_to_expiry !== null && d.days_to_expiry <= 60)).map((d) => (
            <div key={d.key}>{humanize(d.key.replace("driver_", ""))}: {d.missing ? "not on file" : d.days_to_expiry < 0 ? "expired" : `expires in ${d.days_to_expiry} days`}</div>
          ))}
        </div>
      )}

      <div className="flex gap-1 border-b border-slate-200" role="tablist">
        {SCOPES.map((s) => (
          <button key={s.key} role="tab" aria-selected={scope === s.key} onClick={() => setScope(s.key)} data-testid={`dw-scope-${s.key}`}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px ${scope === s.key ? "border-emerald-600 font-medium" : "border-transparent text-slate-600"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {trips && trips.length === 0 && <p className="text-sm text-slate-500" data-testid="dw-no-trips">No trips here.</p>}
      <ul className="space-y-3">
        {(trips || []).map((t) => <Trip key={t.booking_id} trip={t} call={call} />)}
      </ul>

      <ReportPanel call={call} />
      <DocumentUpload onDone={load} />
    </div>
  );
}

function Trip({ trip, call }) {
  const base = `/driver-workspace/trips/${trip.booking_id}`;
  const pending = trip.assignment_state === "PENDING_DRIVER_ACCEPTANCE";
  const decline = () => {
    const reason = window.prompt("Tell the business briefly why you cannot take this trip");
    if (reason) call(() => api.post(`${base}/decline`, { reason }), "Declined. The business will assign someone else.");
  };
  const start = async () => {
    try {
      await api.post(`${base}/start`, {});
      toast.success("Trip started");
      call(() => Promise.resolve(), "Updated");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d?.code === "dispatch_blocked" ? "This trip cannot start yet. Your business has been told what is missing."
        : detailMessage(err));
    }
  };
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-4 space-y-2" data-testid={`dw-trip-${trip.booking_id}`}>
      <div className="flex flex-wrap justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold">{new Date(trip.start_datetime).toLocaleString()}</div>
          <div className="text-sm text-slate-600">{trip.vehicle?.name} {trip.vehicle?.plate_number ? `· ${trip.vehicle.plate_number}` : ""}</div>
        </div>
        <span className="text-xs text-slate-500">{trip.booking_reference}</span>
      </div>
      <div className="text-sm">Pickup: {trip.pickup_location || "to be confirmed"}{trip.destination ? ` · To: ${trip.destination}` : ""}</div>
      <div className="text-sm">Customer: {trip.customer_first_name}{trip.passenger_count ? ` · ${trip.passenger_count} passenger(s)` : ""}</div>
      <div className="text-sm" data-testid="dw-contact">
        {trip.contact_released ? `Contact: ${trip.customer_phone || "not provided"}`
          : `Customer contact is shared from ${new Date(trip.contact_released_at).toLocaleString()}.`}
      </div>
      {trip.special_instructions && <div className="text-sm text-slate-700">Notes: {trip.special_instructions}</div>}
      <div className="flex flex-wrap gap-2 pt-1">
        {pending && <>
          <button className="px-3 py-1.5 rounded-lg btn-brand text-sm" onClick={() => call(() => api.post(`${base}/accept`), "Trip accepted")}>Accept</button>
          <button className="px-3 py-1.5 rounded-lg border text-sm" onClick={decline}>Decline</button>
        </>}
        {!pending && trip.status === "confirmed" && <>
          <button className="px-3 py-1.5 rounded-lg border text-sm" onClick={() => call(() => api.post(`${base}/fit-for-duty`, { fit: true }), "Thanks, recorded")}>I&apos;m fit to drive</button>
          <button className="px-3 py-1.5 rounded-lg border border-rose-300 text-rose-700 text-sm"
                  onClick={() => call(() => api.post(`${base}/fit-for-duty`, { fit: false }), "Recorded. Your business will arrange another driver.")}>I can&apos;t drive safely</button>
          <button className="px-3 py-1.5 rounded-lg btn-brand text-sm" onClick={start}>Start trip</button>
        </>}
        {trip.status === "ongoing" && (
          <button className="px-3 py-1.5 rounded-lg border text-sm" onClick={() => call(() => api.post(`${base}/end`, {}), "Trip ended")}>End trip</button>
        )}
      </div>
    </li>
  );
}

function ReportPanel({ call }) {
  const [kind, setKind] = useState("brakes");
  const [text, setText] = useState("");
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-2">
      <h2 className="font-semibold">Report a problem</h2>
      <div className="flex flex-wrap gap-2 text-sm">
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="px-2 py-1.5 rounded border">
          {["brakes", "steering", "tyres", "lights", "warning_light", "leak", "smoke_or_smell", "other_unsafe"].map((k) =>
            <option key={k} value={k}>{humanize(k)}</option>)}
        </select>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="What is wrong with the vehicle?"
               className="px-2 py-1.5 rounded border flex-1 min-w-0" />
        <button disabled={text.length < 10} className="px-3 py-1.5 rounded border disabled:opacity-50"
                onClick={() => call(() => api.post("/driver-workspace/report-vehicle", { kind, description: text }), "Reported. Do not drive it until it is checked.")}>
          Report vehicle</button>
      </div>
      <button className="text-sm underline text-rose-700"
              onClick={() => call(() => api.post("/driver-workspace/report-safety", { kind: "fatigued" }), "Thank you. Your business will arrange another driver.")}>
        I should not drive right now</button>
    </section>
  );
}

function DocumentUpload({ onDone }) {
  const [file, setFile] = useState(null);
  const [expiry, setExpiry] = useState("");
  const upload = async () => {
    const data = await new Promise((resolve, reject) => {
      const r = new FileReader(); r.onload = () => resolve(String(r.result).split(",")[1]); r.onerror = reject; r.readAsDataURL(file);
    });
    try {
      await api.post("/driver-workspace/documents/driver_licence", {
        data_base64: data, content_type: file.type || "application/pdf", filename: file.name, expiry_date: expiry || null });
      toast.success("Uploaded. Your employer will check it.");
      onDone();
    } catch (err) { toast.error(detailMessage(err)); }
  };
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-2 text-sm">
      <h2 className="font-semibold">Renewed your licence?</h2>
      <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="px-2 py-1.5 rounded border" />
      <button disabled={!file} onClick={upload} className="px-3 py-1.5 rounded border disabled:opacity-50">Upload</button>
    </section>
  );
}
